import cx from 'classnames'
import dayjs from 'dayjs'
import { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'

import { useResponsive } from 'hooks'
import { Message, User } from 'lib/_types'
import { logEvent } from 'lib/analytics'
import { ROUTE_PATH } from 'lib/paths'
import { useCurrentUser } from 'store/api/yay/user'

import { TextWithLink } from 'components'
import Avatar from 'components/Avatar'
import ImageLoader from 'components/ImageLoader'
import Menu from 'components/Menu'
import { Link } from 'features/i18n/router'

import { ChatMessageReplyPreview } from '../ChatMessageReplyPreview'
import { ChatMessageCall } from './ChatMessageCall'
import { ChatMessageMedia } from './ChatMessageMedia'
import { ChatMessageNotify } from './ChatMessageNotify'
import { useChatMessageMenu } from '../hooks'

const SHAKING_TIMEOUT = 500

const ChatMessageComponent = ({
  message,
  messageOwner,
  parentMessageOwner,
  previousMessageOwnerId,
  isGroupChat,
  onPreviewMedia,
  onDeleteMessage,
  onReport,
  onReply,
  onJumpToMessage,
  focus,
}: {
  message: Message
  messageOwner?: User
  parentMessageOwner?: User
  previousMessageOwnerId?: number
  isGroupChat: boolean
  onPreviewMedia: (payload: { type: 'image' | 'video'; url: string }) => void
  onDeleteMessage: (messageId: number) => void
  onReport: (userId: number) => void
  onReply: (message: Message) => void
  onJumpToMessage: (id: number) => void
  focus: boolean
}) => {
  const { t } = useTranslation()
  const { isMobile } = useResponsive()
  const { ref: inViewRef, inView } = useInView({ skip: !focus })
  const [isShaking, setIsShaking] = useState(false)
  const timeout = useRef<NodeJS.Timeout>(null)
  const timestamp = useRef(0)

  const { data: currentUser } = useCurrentUser()

  const isCurrentUserMessage = currentUser?.id === message?.user_id
  const isContinuousMessage = message.user_id === previousMessageOwnerId
  const isShowAvatar = !isCurrentUserMessage && !isContinuousMessage

  const { menu, openActionSheetMenu } = useChatMessageMenu({
    message,
    isCurrentUserMessage,
    onDeleteMessage,
    onReport,
    onReply,
  })

  const onAvatarClick = () => {
    if (!messageOwner) return

    logEvent.user_profile_open({
      target_user_id: messageOwner?.id?.toString?.(),
      from: 'undefined',
    })
  }

  // not show anything unprocessed from other user
  if (
    !isCurrentUserMessage &&
    (message.message_type === 'video' ||
      message.message_type === 'eternal_video') &&
    !message.video_processed
  )
    return null

  useEffect(() => {
    if (focus) {
      clearTimeout(timeout.current)

      if (inView) {
        const now = Date.now()
        if (now - timestamp.current >= SHAKING_TIMEOUT) {
          timestamp.current = now

          setIsShaking(true)
        }
      }

      timeout.current = setTimeout(() => {
        setIsShaking(false)
      }, 500)
    }
  }, [inView, focus])

  useEffect(
    () => () => {
      clearTimeout(timeout.current)
    },
    []
  )

  return (
    <div
      id={`Messages__item--${message.id}`}
      className={cx('Messages__item', {
        'Messages__item--self': isCurrentUserMessage,
        'Messages__item--sending': message?._sending,
        'Messages__item--continuous': isContinuousMessage,
        'Messages__item--shake': isShaking,
      })}
      ref={inViewRef}
    >
      {(() => {
        switch (message?.message_type) {
          case 'owner_kick':
          case 'user_joined':
          case 'user_leave':
          case 'screenshot_warning': {
            return (
              <ChatMessageNotify
                message={message}
                isCurrentUserMessage={isCurrentUserMessage}
              />
            )
          }

          default: {
            return (
              <>
                {isShowAvatar ? (
                  <Link
                    onClick={onAvatarClick}
                    to={messageOwner?.id && ROUTE_PATH.USER(messageOwner?.id)}
                    className="Messages__item__img"
                  >
                    <Avatar user={messageOwner} />
                  </Link>
                ) : (
                  <div></div>
                )}

                <div
                  className="Messages__item__content"
                  onContextMenu={(e) => {
                    if (isMobile) {
                      e.preventDefault()
                      openActionSheetMenu()
                    }
                  }}
                  onDoubleClick={(e) => {
                    if (isMobile) {
                      e.preventDefault()
                      openActionSheetMenu()
                    }
                  }}
                >
                  {isShowAvatar && isGroupChat && (
                    <Link
                      onClick={(e) => {
                        e.stopPropagation()
                        onAvatarClick()
                      }}
                      to={messageOwner?.id && ROUTE_PATH.USER(messageOwner?.id)}
                      className="Messages__item__username"
                    >
                      {messageOwner?.nickname}
                    </Link>
                  )}

                  <div>
                    {message.parent_id && (
                      <ChatMessageReplyPreview
                        message={message}
                        messageOwner={messageOwner}
                        parentMessageOwner={parentMessageOwner}
                        onJumpToMessage={onJumpToMessage}
                      />
                    )}

                    <div className="Messages__item__text">
                      <div
                        className={cx('Messages__item__span', {
                          [`Messages__item__span--${message?.message_type}`]:
                            message?.message_type,
                        })}
                      >
                        {(() => {
                          switch (message?.message_type) {
                            case 'deleted':
                              return isCurrentUserMessage
                                ? t('chat.message.deleted.you')
                                : t('chat.message.deleted.user', {
                                    name:
                                      messageOwner?.nickname ??
                                      t('common.user'),
                                  })

                            case 'text':
                              return (
                                <TextWithLink>{message?.text}</TextWithLink>
                              )

                            case 'gif':
                            case 'sticker':
                              return (
                                <ImageLoader
                                  url={(message?.gif ?? message?.sticker)?.url}
                                  preserveHeight="160px"
                                />
                              )

                            case 'call':
                            case 'individual_call':
                              return <ChatMessageCall message={message} />

                            case 'image':
                            case 'eternal_image':
                            case 'video':
                            case 'eternal_video':
                              return (
                                <ChatMessageMedia
                                  message={message}
                                  isCurrentUserMessage={isCurrentUserMessage}
                                  onPreviewMedia={onPreviewMedia}
                                />
                              )

                            default:
                              return null
                          }
                        })()}
                      </div>
                      <div className="Messages__item__time">
                        {message._sending ? (
                          <img
                            src="/images/icon-sending.svg"
                            className="Messages__item__time__sending"
                            alt="sending"
                          />
                        ) : (
                          <>
                            {!isMobile && menu?.length > 0 && (
                              <Menu
                                arrowPosition={
                                  isCurrentUserMessage ? 'right' : 'left'
                                }
                                panelPosition={
                                  isCurrentUserMessage ? 'left' : 'right'
                                }
                                icon="ellipsis"
                                onToggle={(_, e) => e.preventDefault()}
                                options={menu}
                              />
                            )}

                            <span>
                              {dayjs(message?.created_at * 1000).format('H:mm')}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )
          }
        }
      })()}
    </div>
  )
}

export const ChatMessage = memo(
  ChatMessageComponent,
  (prevProps, nextProps) => {
    if (prevProps?.focus != nextProps?.focus) return false

    const messageCheckList = [
      '_sending',
      'video_url',
      'text',
      'attachment',
      'conference_call',
    ]

    return messageCheckList.every((field) => {
      if (prevProps?.message?.[field] != nextProps?.message?.[field]) {
        return false
      }

      if (
        prevProps?.message?.parent?.[field] !=
        nextProps?.message?.parent?.[field]
      ) {
        return false
      }

      return true
    })
  }
)
